import React, { useCallback } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { DELETE_DEVICES } from "cortex/ui/src/redux-store/devices/actions";
import { DeviceStatusIcon } from "cortex/ui/src/ui/table/component/DeviceStatusIcon";

import BaseDrawer, { ListItem, ListWrapper } from "./BaseDrawer";
import { Device } from "../../model";

interface DeleteDeviceDrawerProps {
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Device>;
  setSelectedItems: (items: ReadonlyArray<Device>) => void;
}
interface UpgradeListItemProps {
  device: Device;
}

export default function DeleteDeviceDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: DeleteDeviceDrawerProps) {
  const dispatch = useAppDispatch();
  const hasThirdPartyDevices = selectedItems.some(
    (device) => device.is_thirdparty,
  );
  const eligibleDevices = selectedItems.filter(
    (device) => !device.is_thirdparty,
  );

  const handleDeleteDevices = useCallback(async () => {
    if (eligibleDevices.length === 0) {
      return;
    }
    const selectedDeviceIds = eligibleDevices.map((device) => device.id);

    try {
      await createPromiseApiAction(dispatch, DELETE_DEVICES, [
        selectedDeviceIds,
      ]);
      showSuccess(
        `Successfully deleted ${selectedDeviceIds.length} device(s).`,
      );
    } catch (e) {
      console.error("Failed to delete devices:", e);
    } finally {
      setSelectedItems([]);
      onCancel();
    }
  }, [dispatch, eligibleDevices, onCancel, setSelectedItems]);

  const drawerHeader = (
    <>
      {hasThirdPartyDevices && (
        <Alert severity="info" title="Third-party devices cannot be deleted.">
          You selected {selectedItems.length} device(s) for deletion, but{" "}
          {selectedItems.length - eligibleDevices.length} of them are
          third-party devices and cannot be deleted. <br />
          They are not included in the list below and will not be affected by
          this action.
        </Alert>
      )}
      <Alert
        dense
        severity="warning"
        title={`You are about to delete ${eligibleDevices.length} device(s).`}
      >
        You will <em>not</em> be able to revert this change. Please double check
        the device list before you proceed.
      </Alert>
    </>
  );

  return (
    <Drawer
      commitColor="error"
      commitLabel="Delete"
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleDeleteDevices}
      open={open}
      padding={0}
      title="Delete Devices"
    >
      <BaseDrawer drawerHeader={drawerHeader}>
        {eligibleDevices.map((device) => (
          <ListWrapper disablePadding key={device.id}>
            <UpgradeListItem device={device} />
          </ListWrapper>
        ))}
      </BaseDrawer>
    </Drawer>
  );
}

const UpgradeListItem = ({ device }: UpgradeListItemProps) => {
  return (
    <ListItem>
      <Stack alignItems="center" direction="row" spacing={2}>
        <DeviceStatusIcon device={device} />
        <Stack flex={1}>
          <Tooltip title={device.device_id}>
            <Link to={`/devices/${device.device_id}`}>
              <Typography color={Colors.gray1000} variant="body1">
                {device.name}
              </Typography>
            </Link>
          </Tooltip>
          <Typography variant="body2">
            <Link to={`/networks/${device.network_id}`}>
              {device.network_name}
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </ListItem>
  );
};
